import { HoldDomain, HoldRuleView } from '@/console/api/types'

/**
 * Hold 규칙 추가 폼의 순수 로직. `HoldCreateForm` 은 이 모듈이 만든 옵션·값·오류만 그리고,
 * domain·kind 분기·검증은 여기에 둔다(브리프 원칙 — 화면 컴포넌트에 계산을 넣지 않는다).
 */
export type HoldFormKind = 'attribute' | 'predicate' | 'maintenance'

export type HoldFieldOption = { value: string; label: string }

const FULFILLMENT_FIELDS: HoldFieldOption[] = [
  { value: 'orderId', label: '주문 ID' },
  { value: 'shipperCode', label: '화주 코드' },
]

// dispatcher 두 domain(fulfillment-dispatcher · 29cm-dispatcher) 과 '*' 는 같은 6개 후보를 쓴다
// (핸드오프 §4.1 — field 후보는 domain 에 따라 갈리되, fulfillment 만 예외).
const DISPATCHER_FIELDS: HoldFieldOption[] = [
  { value: 'order.orderNumber', label: '주문번호' },
  { value: 'order.optionNumber', label: '옵션번호' },
  { value: 'shipper.shipperCode', label: '화주 코드' },
  { value: 'shipper.shipperName', label: '화주명' },
  { value: 'shipper.serviceType', label: '물류 서비스 타입' },
  { value: 'salesChannel.code', label: '판매 채널' },
]

/** domain 이 바뀌면 기준 항목 후보가 갈린다 — `fulfillment` 만 주문 키 2개, 나머지는 dispatcher 6개. */
export const fieldOptionsForDomain = (domain: HoldDomain): HoldFieldOption[] =>
  domain === 'fulfillment' ? FULFILLMENT_FIELDS : DISPATCHER_FIELDS

/** 쉼표·줄바꿈으로 끊고 앞뒤 공백을 지우고 빈 값과 중복을 제거한다. 순서는 입력 순서를 유지한다. */
export const parseHoldValues = (raw: string): string[] => {
  const seen = new Set<string>()
  const result: string[] = []

  raw
    .split(/[,\n]/)
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 0)
    .forEach((piece) => {
      if (!seen.has(piece)) {
        seen.add(piece)
        result.push(piece)
      }
    })

  return result
}

export type HoldFormInput = {
  kind: HoldFormKind
  holdId: string
  domain: HoldDomain
  field: string
  values: string
  from: number | null
  until: number | null
}

export type HoldFormError = { field: 'holdId' | 'values' | 'period'; message: string }

/**
 * maintenance 는 from·toExclusive 가 둘 다 있어야 하고 from < toExclusive 여야 한다.
 * attribute/predicate 는 values 가 (쉼표·줄바꿈 정리 후) 하나 이상이어야 한다 — 서버가 빈 배열을 400 으로 막는다.
 */
export const validateHoldForm = (input: HoldFormInput): HoldFormError[] => {
  const errors: HoldFormError[] = []

  if (!input.holdId.trim()) {
    errors.push({ field: 'holdId', message: '규칙 ID 를 입력하세요.' })
  }

  if (input.kind === 'maintenance') {
    if (input.from === null || input.until === null || input.from >= input.until) {
      errors.push({ field: 'period', message: '시작과 끝(제외)을 모두 입력하고, 끝이 시작보다 뒤여야 합니다.' })
    }
    return errors
  }

  if (parseHoldValues(input.values).length === 0) {
    errors.push({ field: 'values', message: '값을 하나 이상 입력하세요.' })
  }

  return errors
}

/**
 * 검증을 통과한 `HoldFormInput` 을 실제 `HoldRuleView` 모양으로 편다 — POST 요청 바디도 이 모양이다
 * (핸드오프 §4.1). 코드 리뷰 반영(LOGISPRD-12506 Task 6, Finding 1) — 원래 `DispatcherTab.tsx`
 * 안에서 직접 분기하던 걸 순수 함수로 뺐다. `now` 는 호출부가 넘긴다(컴포넌트 안에서 `Date.now()`
 * 를 부르지 않기 위해) — maintenance 인데 시작/끝을 안 넣으면 `now` 로 기본값을 채운다.
 */
export const buildHoldRuleFromForm = (input: HoldFormInput, now: number): HoldRuleView => {
  const holdId = input.holdId.trim()

  if (input.kind === 'maintenance') {
    return {
      type: 'maintenance',
      holdId,
      domain: input.domain,
      from: input.from ?? now,
      toExclusive: input.until ?? now,
    }
  }

  const values = parseHoldValues(input.values)

  if (input.kind === 'predicate') {
    return {
      type: 'predicate',
      holdId,
      domain: input.domain,
      predicate: { op: 'leaf', field: input.field, values },
    }
  }

  return {
    type: 'attr',
    holdId,
    domain: input.domain,
    field: input.field,
    values,
  }
}
